import { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import "./CommandCenter.css";

const AUSTIN_API =
  "https://data.austintexas.gov/resource/9t4d-g238.json?$limit=100";

export default function CommandCenter() {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const [totalCalls, setTotalCalls] = useState("—");
  const [lastHourCalls, setLastHourCalls] = useState("—");
  const [incidents, setIncidents] = useState([]);

  useEffect(() => {
    loadTexasData();
    const interval = setInterval(loadTexasData, 300000);
    return () => clearInterval(interval);
  }, []);

  async function loadTexasData() {
    try {
      const res = await fetch(AUSTIN_API);
      const data = await res.json();

      if (!Array.isArray(data)) throw new Error("Invalid API response");

      // KPIs
      setTotalCalls(data.length);

      const now = Date.now();
      const lastHour = data.filter(
        d =>
          d.time_received &&
          now - new Date(d.time_received).getTime() < 3600000
      );
      setLastHourCalls(lastHour.length);

      // Incidents
      setIncidents(data.slice(0, 6));

      // Chart buckets
      const buckets = {};
      data.forEach(d => {
        if (!d.time_received) return;
        const hour = new Date(d.time_received).getHours();
        buckets[hour] = (buckets[hour] || 0) + 1;
      });

      const labels = Object.keys(buckets).sort((a, b) => a - b);
      const values = labels.map(h => buckets[h]);

      const ctx = chartRef.current;

      if (chartInstance.current) {
        chartInstance.current.data.labels = labels;
        chartInstance.current.data.datasets[0].data = values;
        chartInstance.current.update();
      } else {
        chartInstance.current = new Chart(ctx, {
          type: "line",
          data: {
            labels,
            datasets: [
              {
                data: values,
                borderColor: "#ff4d5a",
                backgroundColor: "rgba(255,77,90,.15)",
                fill: true,
                tension: 0.4
              }
            ]
          },
          options: {
            plugins: { legend: { display: false } },
            scales: {
              x: { ticks: { color: "#9fb3c8" } },
              y: { ticks: { color: "#9fb3c8" } }
            }
          }
        });
      }
    } catch (err) {
      console.error(err);
      setIncidents([]);
    }
  }

  return (
    <>
      <nav>
        <span>Operation Border Lights</span>
        <a href="/">Exit Command Center</a>
      </nav>

      <div className="page">
        <h1>Texas Command Center</h1>
        <p>Live public-safety visibility using approved Texas open data sources.</p>

        <div className="kpis">
          <div className="kpi">
            <span className="status live">Live</span>
            <span>911 Calls (Austin)</span>
            <h2>{totalCalls}</h2>
          </div>

          <div className="kpi">
            <span className="status live">Live</span>
            <span>Calls (Last Hour)</span>
            <h2>{lastHourCalls}</h2>
          </div>

          <div className="kpi">
            <span className="status demo">Demo</span>
            <span>Avg Response</span>
            <h2>—</h2>
          </div>

          <div className="kpi">
            <span className="status demo">Demo</span>
            <span>High Risk Alerts</span>
            <h2>—</h2>
          </div>
        </div>

        <div className="grid">
          <div className="panel">
            <span className="status live">Live</span>
            <h3>Austin 911 Calls (Last 24h)</h3>
            <canvas ref={chartRef} height="140" />
          </div>

          <div className="panel">
            <span className="status live">Live</span>
            <h3>Recent Austin Calls</h3>
            <ul>
              {incidents.length === 0 && <li>Live Texas data unavailable</li>}
              {incidents.map((d, i) => (
                <li key={i}>
                  <strong>{d.problem || "911 Call"}</strong>
                  <br />
                  {d.location || "Austin, TX"}
                  <br />
                  <small>
                    {d.time_received &&
                      new Date(d.time_received).toLocaleTimeString()}
                  </small>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <footer>
        Data source: City of Austin Open Data (Calls for Service).  
        Situational awareness only. Not for emergency response.
      </footer>
    </>
  );
}
